import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    FaLifeRing,
    FaTicketAlt,
    FaRegCommentDots,
    FaBook,
    FaPlus,
    FaCheckCircle,
    FaRegClock,
    FaPaperPlane,
    FaTimes,
    FaChevronRight,
    FaHistory,
    FaQuestionCircle,
    FaLightbulb
} from 'react-icons/fa';
import DataTable from '../../SuperAdmin/shared/DataTable';
import Loader from '../../shared/Loader';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import './TutorDashboardOverview.css';
import '../../SuperAdmin/views/UserModals.css';

const TutorSupport = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isThreadModalOpen, setIsThreadModalOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        subject: '',
        category: 'Technical Issue',
        description: '',
        priority: 'Normal'
    });

    useEffect(() => {
        fetchTickets();
    }, []);

    const fetchTickets = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/support/tickets', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTickets(res.data);
            setLoading(false);
        } catch (err) {
            showError('Sync Error', 'Failed to synchronize with the Support DB.');
            setLoading(false);
        }
    };

    const fetchTicketDetails = async (id) => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/support/tickets/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSelectedTicket(res.data);
            setIsThreadModalOpen(true);
        } catch (err) {
            showError('Thread Error', 'Could not retrieve ticket conversation.');
        }
    };

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        showLoading('Raising Support Ticket...');
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post('/api/support/tickets', formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            closeLoading();
            showSuccess('Ticket Raised', `Ticket #${res.data._id.substring(18)} created successfully.`);
            setIsCreateModalOpen(false);
            setFormData({ subject: '', category: 'Technical Issue', description: '', priority: 'Normal' });
            fetchTickets();
        } catch (err) {
            closeLoading();
            showError('Submission Error', 'Failed to transmit ticket details.');
        }
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!replyText.trim()) return;

        showLoading('Transmitting Reply...');
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/support/tickets/${selectedTicket._id}/reply`,
                { messageText: replyText },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setReplyText('');
            closeLoading();
            fetchTicketDetails(selectedTicket._id);
        } catch (err) {
            closeLoading();
            showError('Transmission Error', 'Failed to send your message.');
        }
    };

    const ticketColumns = [
        {
            header: 'Ticket ID', key: '_id', width: '15%', render: (row) => (
                <strong style={{ color: 'var(--primary-blue)' }}>#{row._id.substring(18).toUpperCase()}</strong>
            )
        },
        {
            header: 'Issue & Sector', key: 'subject', width: '30%', render: (row) => (
                <div>
                    <div style={{ color: '#0F172A', fontWeight: '600' }}>{row.subject}</div>
                    <div style={{ fontSize: '11px', color: '#64748B' }}>{row.category}</div>
                </div>
            )
        },
        {
            header: 'Priority', key: 'priority', width: '10%', render: (row) => (
                <span style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: row.priority === 'High' ? '#EF4444' : row.priority === 'Normal' ? '#F59E0B' : '#64748B'
                }}>
                    {row.priority}
                </span>
            )
        },
        {
            header: 'Updated', key: 'updatedAt', width: '15%', render: (row) => (
                <div style={{ fontSize: '12px', color: '#64748B' }}>
                    {new Date(row.updatedAt).toLocaleDateString()}
                </div>
            )
        },
        {
            header: 'Status', key: 'status', width: '15%', render: (row) => (
                <span className={`status-badge ${row.status === 'Resolved' ? 'active' : row.status === 'In Progress' ? 'pending' : 'pending'}`} style={{
                    background: row.status === 'In Progress' ? 'rgba(59, 130, 246, 0.1)' : '',
                    color: row.status === 'In Progress' ? '#3B82F6' : ''
                }}>
                    {row.status}
                </span>
            )
        },
        {
            header: 'Engagement', key: 'actions', width: '15%', render: (row) => (
                <div className="action-buttons">
                    <button className="action-btn-gold sm" title="Open Conversation Thread" onClick={() => fetchTicketDetails(row._id)}>
                        <FaRegCommentDots />
                    </button>
                </div>
            )
        }
    ];

    if (loading) return <Loader />;

    return (
        <div className="dashboard-overview fade-in">
            <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                <div>
                    <h1 className="page-title">Support & Tech Ops Centre</h1>
                    <p className="page-subtitle">Direct line to Super Admin for technical issues, payroll queries, and resource requests.</p>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                        className="secondary-btn pulse-on-hover"
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        onClick={() => setIsFaqModalOpen(true)}
                    >
                        <FaBook /> FAQ Hub
                    </button>
                    <button className="primary-btn pulse-on-hover" onClick={() => setIsCreateModalOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaPlus /> Raise Ticket
                    </button>
                </div>
            </div>

            <div className="kpi-grid" style={{ marginBottom: '32px' }}>
                <div className="kpi-card">
                    <div className="kpi-header">
                        <span className="kpi-title">Active Queries</span>
                        <div className="kpi-icon-wrapper" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6' }}><FaTicketAlt /></div>
                    </div>
                    <h2 className="kpi-value">{tickets.filter(t => t.status !== 'Resolved').length}</h2>
                    <div className="kpi-trend trend-up">
                        <FaRegClock /> <span>Avg. 2h Response Time</span>
                    </div>
                </div>
                <div className="kpi-card">
                    <div className="kpi-header">
                        <span className="kpi-title">Success Rate</span>
                        <div className="kpi-icon-wrapper" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10B981' }}><FaCheckCircle /></div>
                    </div>
                    <h2 className="kpi-value">98.4%</h2>
                    <div className="kpi-trend trend-neutral">
                        <span>Resolved by Central Authority</span>
                    </div>
                </div>
            </div>

            <DataTable
                title="Mission Critical Support History"
                columns={ticketColumns}
                data={tickets}
                filterOptions={[
                    { label: 'Full Ledger', value: '' },
                    { label: 'Active Issues', value: 'Open' },
                    { label: 'Work In Progress', value: 'In Progress' },
                    { label: 'Archived/Resolved', value: 'Resolved' }
                ]}
            />

            {/* Raise Ticket Modal - Standardized */}
            {isCreateModalOpen && createPortal(
                <div className={`modal-overlay ${isCreateModalOpen ? 'open' : ''}`} onClick={() => setIsCreateModalOpen(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '900px' }}>
                        <div className="modal-header">
                            <h2><FaLifeRing /> Support Case Architect</h2>
                            <button className="modal-close-btn" onClick={() => setIsCreateModalOpen(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleSubmit} className="modal-form">
                                {/* Section 1: Classification */}
                                <div className="form-group full-width">
                                    <h3 style={{ color: 'var(--primary-blue)', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px', margin: 0 }}>1. Incident Classification</h3>
                                </div>

                                <div className="form-group">
                                    <label className="premium-label">Incident Sector</label>
                                    <select
                                        name="category" value={formData.category} onChange={handleFormChange}
                                        className="glass-input" required
                                    >
                                        <option value="Technical Issue">Platform Bug / Technical Error</option>
                                        <option value="HR / Payroll">HR, Salary, or Payroll Dispute</option>
                                        <option value="Software Reqs">Software & Tool Requisitions</option>
                                        <option value="Student Reporting">Reporting Specific Behaviour</option>
                                        <option value="General Query">Administrative Enquiry</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="premium-label">Criticality Level</label>
                                    <select
                                        name="priority" value={formData.priority} onChange={handleFormChange}
                                        className="glass-input"
                                    >
                                        <option value="Low">Low (Administrative enquiry)</option>
                                        <option value="Normal">Normal (Requires attention today)</option>
                                        <option value="High">High (Urgent - Operational Blocker)</option>
                                    </select>
                                </div>

                                {/* Section 2: Details */}
                                <div className="form-group full-width">
                                    <h3 style={{ color: 'var(--primary-blue)', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px', margin: '16px 0 0 0' }}>2. Technical Briefing</h3>
                                </div>

                                <div className="form-group full-width">
                                    <label className="premium-label">Subject of Concern</label>
                                    <input
                                        type="text" name="subject" value={formData.subject} onChange={handleFormChange}
                                        className="glass-input" required placeholder="Brief summary of the issue..."
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label className="premium-label">Comprehensive Description</label>
                                    <textarea
                                        name="description" value={formData.description} onChange={handleFormChange}
                                        className="glass-input resize-none" rows="5" required placeholder="Provide granular details for the Admin team..."
                                    ></textarea>
                                </div>

                                <div className="modal-actions">
                                    <button type="button" className="action-btn-secondary" onClick={() => setIsCreateModalOpen(false)}>Discard</button>
                                    <button type="submit" className="action-btn-gold pulse-on-hover">
                                        Deploy Ticket <FaChevronRight style={{ marginLeft: '10px' }} />
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Conversation Thread Modal - MASTERPIECE REDESIGN */}
            {isThreadModalOpen && selectedTicket && createPortal(
                <div className={`modal-overlay ${isThreadModalOpen ? 'open' : ''}`} onClick={() => setIsThreadModalOpen(false)} style={{ background: 'rgba(2, 6, 23, 0.85)', backdropFilter: 'blur(12px)' }}>
                    <div className="modal-content fade-in" onClick={e => e.stopPropagation()} style={{
                        maxWidth: '1000px',
                        height: '92vh',
                        display: 'flex',
                        flexDirection: 'column',
                        background: '#fff',
                        borderRadius: '32px',
                        overflow: 'hidden',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                        {/* Executive Header */}
                        <div className="modal-header" style={{
                            padding: '32px 40px',
                            background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
                            color: '#fff',
                            borderBottom: 'none',
                            position: 'relative',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    background: 'rgba(212, 175, 55, 0.15)',
                                    color: 'var(--primary-gold)',
                                    borderRadius: '18px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '28px',
                                    border: '1px solid rgba(212, 175, 55, 0.3)',
                                    boxShadow: '0 0 20px rgba(212, 175, 55, 0.1)'
                                }}>
                                    <FaTicketAlt />
                                </div>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                                        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 800, letterSpacing: '-0.5px', color: '#fff' }}>Executive Mitigation Center</h2>
                                        <span style={{
                                            background: selectedTicket.status === 'Resolved' ? '#10B981' : '#F59E0B',
                                            color: '#fff',
                                            padding: '4px 12px',
                                            borderRadius: '20px',
                                            fontSize: '11px',
                                            fontWeight: 800,
                                            textTransform: 'uppercase'
                                        }}>{selectedTicket.status}</span>
                                    </div>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#94A3B8', fontWeight: 500 }}>
                                        Channel ID: <span style={{ color: 'var(--primary-gold)' }}>#{selectedTicket._id.toUpperCase()}</span> • Domain: {selectedTicket.category}
                                    </p>
                                </div>
                            </div>
                            <button
                                className="modal-close-btn"
                                onClick={() => setIsThreadModalOpen(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        {/* Subject Intelligence Strip */}
                        <div style={{ padding: '16px 40px', background: '#F1F5F9', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '1px' }}>Active Subject:</span>
                            <span style={{ fontSize: '14px', fontWeight: 600, color: '#0F172A' }}>{selectedTicket.subject}</span>
                        </div>

                        {/* Communication Vault (Chat Area) */}
                        <div className="modal-body" style={{ flex: 1, overflowY: 'auto', background: '#F8FAFC', padding: '40px' }}>
                            <div className="chat-container" style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                                {selectedTicket.messages.map((msg, idx) => {
                                    const isAdmin = msg.senderModel === 'super_admin';
                                    return (
                                        <div key={idx} style={{
                                            alignSelf: isAdmin ? 'flex-start' : 'flex-end',
                                            maxWidth: '80%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: isAdmin ? 'flex-start' : 'flex-end',
                                            animation: 'fadeInUp 0.4s ease-out forwards'
                                        }}>
                                            <div style={{
                                                background: isAdmin ? '#fff' : 'linear-gradient(135deg, var(--primary-blue) 0%, #1E3A8A 100%)',
                                                color: isAdmin ? '#1E293B' : '#fff',
                                                padding: '20px 24px',
                                                borderRadius: '24px',
                                                borderBottomLeftRadius: isAdmin ? '4px' : '24px',
                                                borderBottomRightRadius: isAdmin ? '24px' : '4px',
                                                boxShadow: isAdmin ? '0 10px 15px -3px rgba(0, 0, 0, 0.05)' : '0 10px 15px -3px rgba(30, 58, 138, 0.2)',
                                                fontSize: '15px',
                                                lineHeight: '1.7',
                                                border: isAdmin ? '1px solid #E2E8F0' : 'none',
                                                position: 'relative'
                                            }}>
                                                {msg.messageText}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
                                                <span style={{
                                                    fontSize: '11px',
                                                    color: isAdmin ? 'var(--primary-blue)' : '#64748B',
                                                    fontWeight: 800,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    {isAdmin ? 'ADMINISTRATIVE COMMAND' : 'FIELD OPERATIONS (YOU)'}
                                                </span>
                                                <span style={{ fontSize: '11px', color: '#CBD5E1' }}>•</span>
                                                <span style={{ fontSize: '11px', color: '#94A3B8', fontWeight: 500 }}>
                                                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Command Input Area */}
                        {selectedTicket.status !== 'Resolved' ? (
                            <div className="modal-actions" style={{ padding: '20px 40px 28px', background: '#fff', borderTop: '1px solid #E2E8F0', boxShadow: '0 -10px 20px rgba(0,0,0,0.02)', marginTop: 0 }}>
                                <form onSubmit={handleReply} style={{ width: '100%', display: 'flex', gap: '20px', alignItems: 'center' }}>
                                    <div style={{ flex: 1, position: 'relative' }}>
                                        <input
                                            type="text"
                                            className="glass-input"
                                            placeholder="Transmit technical response to Command Center..."
                                            value={replyText}
                                            onChange={(e) => setReplyText(e.target.value)}
                                            style={{
                                                height: '64px',
                                                borderRadius: '20px',
                                                padding: '0 30px',
                                                fontSize: '16px',
                                                background: '#F1F5F9',
                                                border: '2px solid transparent',
                                                transition: 'all 0.3s ease'
                                            }}
                                        />
                                    </div>
                                    <button type="submit" className="action-btn-gold" style={{
                                        width: '72px',
                                        height: '64px',
                                        borderRadius: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '24px',
                                        boxShadow: '0 10px 15px -3px rgba(212, 175, 55, 0.3)',
                                        transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                                    }}>
                                        <FaPaperPlane />
                                    </button>
                                </form>
                            </div>
                        ) : (
                            <div style={{
                                padding: '32px',
                                textAlign: 'center',
                                background: 'linear-gradient(to right, #F1F5F9, #F8FAFC, #F1F5F9)',
                                color: '#475569',
                                fontSize: '15px',
                                fontWeight: 600,
                                borderTop: '1px solid #E2E8F0',
                                letterSpacing: '0.5px'
                            }}>
                                <FaCheckCircle style={{ marginRight: '10px', color: '#10B981', fontSize: '18px' }} />
                                Operational Mission Finalized. Communication channel archived.
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}

            {/* FAQ HUD MODAL - NEW INTEGRATION */}
            {isFaqModalOpen && createPortal(
                <div className={`modal-overlay ${isFaqModalOpen ? 'open' : ''}`} onClick={() => setIsFaqModalOpen(false)}>
                    <div className="modal-content glass-card fade-in" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
                        <div className="modal-header">
                            <h2><FaQuestionCircle style={{ marginRight: '12px', color: 'var(--primary-blue)' }} /> Tutor Knowledge Intelligence Hub</h2>
                            <button className="modal-close-btn" onClick={() => setIsFaqModalOpen(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body" style={{ padding: '32px' }}>
                            <div className="faq-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                {[
                                    { q: "How do I mark course as completed?", a: "Once all modules are delivered, go to Course Management and update status to 'Completed'. This locks attendance." },
                                    { q: "Where can I download batch reports?", a: "Navigate to 'Student Performance' or 'Attendance Records' and use the 'Export' buttons at the top right." },
                                    { q: "How to raise a payroll query?", a: "Use the 'Raise Ticket' button in this Support Center and select 'HR / Payroll' as the critical sector." },
                                    { q: "Live session technical error?", a: "Check your bandwidth first. If issue persists, raise a 'High' priority technical ticket immediately." },
                                    { q: "Student not visible in roster?", a: "Ensure the student has completed their enrollment payment. Only active enrollees appear in your command center." },
                                    { q: "How to update LMS content?", a: "Go to LMS Modules section, select your course, and use the 'Edit' action on specific modules." }
                                ].map((item, i) => (
                                    <div key={i} style={{ background: '#F8FAFC', padding: '16px', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                                        <div style={{ fontWeight: 700, color: '#0F172A', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <FaLightbulb color="var(--primary-gold)" /> {item.q}
                                        </div>
                                        <p style={{ margin: 0, fontSize: '13px', color: '#64748B', lineHeight: '1.6' }}>{item.a}</p>
                                    </div>
                                ))}
                            </div>
                            <div style={{ marginTop: '32px', padding: '20px', background: 'rgba(0, 44, 95, 0.05)', borderRadius: '16px', textAlign: 'center' }}>
                                <p style={{ margin: 0, color: '#002C5F', fontWeight: 600 }}>Still need operational assistance?</p>
                                <button
                                    className="primary-btn sm"
                                    onClick={() => {
                                        setIsFaqModalOpen(false);
                                        setIsCreateModalOpen(true);
                                    }}
                                    style={{ marginTop: '12px', width: '100%', justifyContent: 'center' }}
                                >
                                    Speak to Command Center <FaChevronRight style={{ marginLeft: '8px' }} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default TutorSupport;

